import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/admin-layout';
import { Card, Table, Th, Td, Button, Input, Badge } from '../components/ui';
import { getSystemSettings, updateSystemSetting } from '../features/system-settings/api';
import type { SystemSetting } from '@app/shared';

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SystemSettingsPage() {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SystemSetting | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSystemSettings();
      setSettings(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const openEditor = (s: SystemSetting) => {
    setEditing(s);
    setEditValue(s.nilai ?? '');
    setEditReason('');
    setConfirmChecked(false);
    setSaveError(null);
    setSuccessMsg(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setEditValue('');
    setEditReason('');
    setConfirmChecked(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!confirmChecked) {
      setSaveError('Anda harus mencentang konfirmasi perubahan');
      return;
    }
    if (!editReason.trim() || editReason.trim().length < 3) {
      setSaveError('Alasan minimal 3 karakter');
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      const updated = await updateSystemSetting(
        editing.kunci,
        editValue || null,
        editReason.trim(),
      );
      setSettings((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
      setSuccessMsg(`Pengaturan "${editing.kunci}" berhasil diperbarui`);
      setTimeout(() => setSuccessMsg(null), 3000);
      closeEditor();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Pengaturan Sistem">
      {successMsg && (
        <div
          style={{
            background: '#DCFCE7',
            color: '#166534',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{successMsg}</span>
          <button
            onClick={() => setSuccessMsg(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: '#166534',
            }}
          >
            ×
          </button>
        </div>
      )}

      <Card>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748B', padding: '2rem' }}>Memuat...</p>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#DC2626' }}>{error}</p>
            <Button onClick={fetchSettings} variant="secondary">
              Coba Lagi
            </Button>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Kunci</Th>
                <Th>Nilai</Th>
                <Th>Deskripsi</Th>
                <Th>Terakhir Diperbarui</Th>
                <Th style={{ textAlign: 'center' }}>Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {settings.length === 0 ? (
                <tr>
                  <Td colSpan={5} style={{ textAlign: 'center', color: '#64748B' }}>
                    Belum ada pengaturan sistem
                  </Td>
                </tr>
              ) : (
                settings.map((s) => (
                  <tr key={s.id}>
                    <Td>
                      <code
                        style={{
                          background: '#F1F5F9',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8125rem',
                        }}
                      >
                        {s.kunci}
                      </code>
                    </Td>
                    <Td>
                      {s.nilai ? (
                        <span style={{ wordBreak: 'break-word' }}>{s.nilai}</span>
                      ) : (
                        <Badge color="gray">kosong</Badge>
                      )}
                    </Td>
                    <Td style={{ color: '#64748B', fontSize: '0.8125rem', maxWidth: '300px' }}>
                      {s.deskripsi ?? '-'}
                    </Td>
                    <Td style={{ fontSize: '0.8125rem', color: '#64748B' }}>
                      {formatDateTime(s.diperbarui_pada)}
                    </Td>
                    <Td style={{ textAlign: 'center' }}>
                      <Button onClick={() => openEditor(s)} variant="secondary">
                        Edit
                      </Button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Edit Modal */}
      {editing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeEditor();
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '520px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem' }}>Edit Pengaturan</h2>
            <p style={{ margin: '0 0 1rem', color: '#64748B', fontSize: '0.875rem' }}>
              <code
                style={{
                  background: '#F1F5F9',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '0.25rem',
                }}
              >
                {editing.kunci}
              </code>
              {editing.deskripsi && (
                <span style={{ display: 'block', marginTop: '0.25rem' }}>{editing.deskripsi}</span>
              )}
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.375rem',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: '#374151',
                }}
              >
                Nilai saat ini
              </label>
              <div
                style={{
                  padding: '0.5rem',
                  background: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  color: '#64748B',
                  marginBottom: '0.5rem',
                  wordBreak: 'break-word',
                }}
              >
                {editing.nilai ?? '(kosong)'}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="edit-value"
                style={{
                  display: 'block',
                  marginBottom: '0.375rem',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: '#374151',
                }}
              >
                Nilai baru
              </label>
              <Input
                id="edit-value"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="Masukkan nilai baru"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="edit-reason"
                style={{
                  display: 'block',
                  marginBottom: '0.375rem',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: '#374151',
                }}
              >
                Alasan perubahan <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <Input
                id="edit-reason"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Jelaskan alasan perubahan (min. 3 karakter)"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div
              style={{
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
              }}
            >
              <input
                type="checkbox"
                id="confirm-edit"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                style={{ marginTop: '0.125rem' }}
              />
              <label
                htmlFor="confirm-edit"
                style={{ fontSize: '0.8125rem', color: '#374151', cursor: 'pointer' }}
              >
                Saya memahami bahwa perubahan ini akan tercatat di audit log dan mempengaruhi
                perilaku sistem. Perubahan tidak dapat dibatalkan secara otomatis.
              </label>
            </div>

            {saveError && (
              <div
                style={{
                  background: '#FEE2E2',
                  color: '#991B1B',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  marginBottom: '1rem',
                  fontSize: '0.8125rem',
                }}
              >
                {saveError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button onClick={closeEditor} variant="secondary" disabled={saving}>
                Batal
              </Button>
              <Button onClick={handleSave} disabled={saving || !confirmChecked}>
                {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
